'use strict';

const Swiper = require('swiper/bundle').Swiper;
const scrollToElement = require('../components/scrollToElement');
const scrollToElementInContainer = require('../components/scrollToElementInContainer');
const breakpoints = require('../breakpoints');
const imageUrl = require('../imageUrl');
const initPopOver = require('../components/initPopOver');
const productTilePlaceholder = require('../components/productTilePlaceholder');
const throttle = require('lodash/throttle');
const noUiSlider = require('nouislider');
const { variantImgHover } = require('../product/variantImgHover');
const { getTranslation } = require('../common/jsTranslations');
const { handleSearchRedirect } = require('../components/search-helper');

let lastAppliedRefinementId = null;

/**
 * Update lastAppliedRefinementId based on which refinement activated search.
 * If no element, reset value.
 * @param {Element|null} searchTriggerElement - element that activated search
 */
function updateLastAppliedRefinementId(searchTriggerElement) {
    lastAppliedRefinementId = searchTriggerElement
        ?.closest('.js-refinement')
        ?.getAttribute('data-refinement-attribute-id');
}

/**
 * Applies placeholder loader on refinement.
 * Warn: it's modifying refinement's content.
 * @param {Element} refinement - refinement element
 */
function refinementPlaceholder(refinement) {
    const btnToggle = refinement?.querySelector('.js-bs-toggle-collapse');
    const toggleContentSelector = btnToggle?.getAttribute('data-bs-target');
    const toggleContent = toggleContentSelector ? refinement?.querySelector(toggleContentSelector) : null;
    const refinementBooleanButtons = refinement.querySelectorAll('.js-refinement-boolean-btn');

    if (!refinement || !btnToggle || !toggleContent) {
        return;
    }

    // idea: applying height style on refinement ensures no jumps (in case placeholder's height is different)
    const currentHeight = refinement.getBoundingClientRect().height;
    refinement.style.height = `${currentHeight}px`;

    refinement.classList.add('placeholder-glow');
    btnToggle.innerHTML = '<div class="col-12 placeholder">&nbsp;</div>';

    if (refinementBooleanButtons.length) {
        refinementBooleanButtons?.forEach((btn) => {
            btn.outerHTML = '<div class="col-8 m-3 placeholder">&nbsp;</div>';
        });
        const showMore = refinement.querySelector('.js-refinement-showmore-link');
        if (showMore) {
            showMore.outerHTML = '<div class="col-4 mt-2 placeholder">&nbsp;</div>';
        }
    } else {
        const isRangeRefinement = refinement?.querySelector('.js-sliders-control');
        if (isRangeRefinement) {
            toggleContent.innerHTML = [
                '<div class="col-12 placeholder my-2 mb-2"></div>',
                '<div class="col-5 placeholder py-5"></div>',
                '<div class="col-5 offset-2 placeholder py-5"></div>'
            ].join('');
        }
    }
}

/**
 * Set attribute value from the url
 *
 * @param {string}  url - URL from the attribute has to be set
 * @param {string}  attributeName - Name of the attribute
 * @param {string}  attributeValue - Value of the attribute
 * @return {string} url after setting the attribute and value
 */
function addAttributeToUrl(url, attributeName, attributeValue) {
    let urlObj = new URL(url, window.location.origin);
    let params = new URLSearchParams(urlObj.search);
    params.set(attributeName, attributeValue);
    urlObj.search = params.toString();
    return urlObj.toString();
}

/**
 * Update DOM elements with Ajax results
 *
 * @param {Object} $results - jQuery DOM element
 * @param {string} selector - DOM element to look up in the $results
 * @return {undefined}
 */
function updateDom($results, selector) {
    var $updates = $results.find(selector);
    $(selector).empty().html($updates.html());
}

function scrollRefinementIntoViewport(refinement) {
    const refinementBar = document.querySelector('.refinement-bar');
    if (!refinementBar || !refinement) {
        return;
    }

    scrollToElementInContainer(refinement, refinementBar);
}

function getNextSiblingsHeight(el) {
    let total = 0;
    let next = el.nextElementSibling;
    while (next) {
        total += next.offsetHeight;
        next = next.nextElementSibling;
    }
    return total;
}

/**
 * Returns refinement top position relative to container viewport.
 * - if refinement is above container's viewport => return negative offset
 * - if refinement is in container's viewport => return value between 0 (if 1st in viewport) and container height
 * - if refinement is below container's viewport => return value bigger then container height
 *
 * Idea: before search grab position and apply it after search
 *
 * @param {string} refinementId - id of refinement
 * @returns {number|null} - offset
 */
function getRefinementTopOffset(refinementId) {
    if (!refinementId) {
        return null;
    }

    const refinement = document.querySelector(`.js-refinement[data-refinement-attribute-id="${refinementId}"]`);
    const refinementBar = document.querySelector('.refinement-bar');
    if (!refinement || !refinementBar) {
        return null;
    }

    return refinement.getBoundingClientRect().top - refinementBar.getBoundingClientRect().top;
}

function refinementHandleShowMore(refinement, showMoreBtn) {
    if (!refinement || !showMoreBtn) {
        return;
    }
    refinement.querySelectorAll("[data-showmore='true']").forEach((item) => {
        item.classList.remove('d-none');
    });
    showMoreBtn.dataset.showmoreStatus = 'false';
    showMoreBtn.innerText = `${showMoreBtn.dataset.showlessText}`;
}

function handleSeoTopTruncation() {
    const seoTopContent = document.querySelector('.js-seo-content-top');
    const btn = document.querySelector('.js-btn-seo-content-top-truncation');

    if (!seoTopContent) {
        return;
    }

    const isContentTruncated = seoTopContent.scrollHeight > seoTopContent.clientHeight;
    if (!isContentTruncated) {
        return;
    }

    btn.classList.remove('pe-none', 'invisible');
    btn.addEventListener('click', () => {
        const currentlyTruncated = seoTopContent.getAttribute('data-truncated') === 'true';
        if (currentlyTruncated) {
            btn.textContent = getTranslation('label.readless');
        } else {
            btn.textContent = getTranslation('label.readmore');
        }
        seoTopContent.setAttribute('data-truncated', (!currentlyTruncated).toString());
    });
}

/**
 * Keep refinement panes expanded/collapsed after Ajax refresh.
 * Keep last applied refinement in viewport.
 *
 * @param {Object} $results - jQuery DOM element
 * @return {undefined}
 */
function handleRefinements($results) {
    const lastSearchRefinementTopOffset = getRefinementTopOffset(lastAppliedRefinementId);
    document.querySelectorAll('.js-refinement .js-refinement-showmore-link[data-showmore-status="false"]').forEach(function (expandedFilterShowMoreBtn) {
        const attributeId = expandedFilterShowMoreBtn.closest('[data-refinement-attribute-id]')?.getAttribute('data-refinement-attribute-id');
        if (!attributeId) {
            return;
        }

        let refinementInAjaxData = $results.find(`.js-refinement [data-refinement-attribute-id="${attributeId}"]`);
        refinementInAjaxData = refinementInAjaxData.length ? refinementInAjaxData[0] : null;
        const refinementShowMoreInAjaxData = refinementInAjaxData.querySelector('.js-refinement-showmore-link');
        if (!refinementShowMoreInAjaxData) {
            return;
        }

        refinementHandleShowMore(refinementInAjaxData, refinementShowMoreInAjaxData);
    });
    updateDom($results, '.js-refinements');
    document.dispatchEvent(new CustomEvent('search:apply-filter-states'));

    if (lastAppliedRefinementId) {
        const lastAppliedRefinement = document.querySelector(`.js-refinement[data-refinement-attribute-id="${lastAppliedRefinementId}"]`);
        const refinementBar = document.querySelector('.refinement-bar');
        if (lastAppliedRefinement && refinementBar) {
            // note: current approach (next siblings) assumes "filters" is last section in refinement bar (to be able to reach end by going sibling by sibling)
            // e.g. "category" section prevents us from using prev sibling approach
            const nextRefinementsTotalHeight = getNextSiblingsHeight(lastAppliedRefinement.closest('.card-body'));
            const topPositionFirstInScrollbar = refinementBar.scrollHeight - (nextRefinementsTotalHeight + lastAppliedRefinement.clientHeight);
            let top = topPositionFirstInScrollbar;
            if (lastSearchRefinementTopOffset) {
                top -= lastSearchRefinementTopOffset;
            }
            refinementBar.scrollTo({
                top: top
            });
        }
    }
}

function getUrlParameters() {
    const params = new URLSearchParams(window.location.search);
    const queryParams = {};

    params.forEach((value, key) => {
        queryParams[key] = value;
    });

    return queryParams;
}

function shouldAddNoindexNofollow() {
    const params = getUrlParameters();
    const noindexNofollowParams = ['pmax', 'pmin', 'prefn1', 'srule'];

    if (window.location.pathname.includes('/search')) {
        return true;
    }

    if (params && params.page && Object.keys(params).length === 1) {
        return false;
    }

    return Object.keys(params).some(param => noindexNofollowParams.includes(param));
}

function updateNoindexNofollow() {
    const shouldAddMetaTag = shouldAddNoindexNofollow();
    const params = getUrlParameters();
    let metaTag = document.querySelector('meta[name="robots"]');

    if (shouldAddMetaTag) {
        if (!metaTag) {
            metaTag = document.createElement('meta');
            metaTag.name = 'robots';
            document.head.appendChild(metaTag);
        }
        metaTag.content = 'noindex,nofollow';
    } else if (params.page) {
        if (!metaTag) {
            metaTag = document.createElement('meta');
            metaTag.name = 'robots';
            document.head.appendChild(metaTag);
        }
        metaTag.content = 'index,follow';
    } else if (metaTag) {
        document.head.removeChild(metaTag);
    }
}

/**
 * Updates the AJAX URL and updates the browser history.
 *
 * @param {JQuery} $results - The jQuery object containing the results.
 * @param {string} srule - for sort rule
 * @param {Object} additionalDispatchData - additional event dispatch data
 * @important .grid-footer can be a root element of $results (e.g. Search-UpdateGrid response),
 * so it is wrapped in a container first to make it reliably findable via .find() regardless
 * of the response structure.
 * @return {void} This function does not return anything.
 */
function updateAjaxURL($results, srule, additionalDispatchData) {
    let updatedURL = $results.find('.permalink')[0]?.value || '';
    if (updatedURL) {
        if (srule) {
            updatedURL = addAttributeToUrl(updatedURL, 'srule', srule);
        }
        window.history.pushState('', '', updatedURL);

        const pageElement = document.querySelector('.page');
        if (pageElement) {
            pageElement.dataset.querystring = window.location.search.substring(1);
        }

        const $canonical = $('link[rel="canonical"]').attr('href', updatedURL);

        const hreflangURLs = $('<div>').append($results).find('.grid-footer').data('hreflang-urls');

        if (hreflangURLs && Object.keys(hreflangURLs).length) {
            $('link[rel="alternate"][hreflang]').remove();
            Object.keys(hreflangURLs).forEach((locale) => {
                $canonical.before($('<link>', { rel: 'alternate', href: hreflangURLs[locale], hreflang: locale }));
            });
        }

        updateNoindexNofollow();

        const eventData = {
            detail: {
                url: updatedURL,
                additionalDispatchData
            }
        };
        document.dispatchEvent(new CustomEvent('search:update-ajax-url', eventData));
    } else {
        console.error('updateAjaxURL new url is empty');
    }
}

/**
 * Syncs the mobile applied-filters accordion with the desktop filter-bar buttons.
 * Clones filter buttons from the desktop dropdown into the mobile accordion list
 * and toggles the accordion visibility.
 */
function syncMobileAppliedFilters() {
    var container = document.querySelector('.js-mobile-applied-filters');
    var list = container && container.querySelector('.js-mobile-applied-filters-list');
    if (!container || !list) {
        return;
    }

    var desktopButtons = document.querySelectorAll('.filter-bar .js-selected-filters-dropdown-menu .js-filter-value-btn');
    list.innerHTML = '';

    document.querySelectorAll('.js-mobile-applied-filters-count').forEach(function (countEl) {
        countEl.textContent = desktopButtons.length ? ` (${desktopButtons.length})` : '';
    });

    var actionsBar = document.querySelector('.js-mobile-filter-actions');

    if (!desktopButtons.length) {
        container.classList.add('d-none');
        if (actionsBar) {
            actionsBar.classList.add('d-none');
        }
        return;
    }

    desktopButtons.forEach(function (btn) {
        var li = document.createElement('li');
        li.appendChild(btn.cloneNode(true));
        list.appendChild(li);
    });

    container.classList.remove('d-none');
    if (actionsBar) {
        actionsBar.classList.remove('d-none');
    }
}

/**
 * Parse Ajax results and updated select DOM elements
 *
 * @param {string} response - Ajax response HTML code
 * @param {string} srule - for sort rule
 * @param {Object} additionalDispatchDataOnUpdateUrl - additional event dispatch data
 * @return {undefined}
 */
function parseResults(response, srule, additionalDispatchDataOnUpdateUrl) {
    var $results = $(response);
    var specialHandlers = {
        '.js-refinements': handleRefinements
    };

    // Update DOM elements that do not require special handling
    [
        '.grid-header',
        '.header-bar',
        '.header.page-title',
        '.product-grid',
        '.show-more',
        '.filter-bar',
        '.js-sort-menu',
        '.js-results-count',
        '.js-search-no-products-result'
    ].forEach(function (selector) {
        updateDom($results, selector);
    });

    [
        '.js-sort-menu',
        '.js-view-toggle-wrapper',
        '.js-results-count'
    ].forEach(function (selector) {
        const resultElementHidden = $results.find(selector)?.hasClass('d-none');
        $(selector)?.toggleClass('d-none', resultElementHidden);
    });

    Object.keys(specialHandlers).forEach(function (selector) {
        specialHandlers[selector]($results);
    });

    syncMobileAppliedFilters();

    updateAjaxURL($results, srule, additionalDispatchDataOnUpdateUrl);
}

/**
 * This function retrieves another page of content to display in the content search grid
 * @param {JQuery} $element - the jquery element that has the click event attached
 * @param {JQuery} $target - the jquery element that will receive the response
 * @return {undefined}
 */
function getContent($element, $target) {
    var showMoreUrl = $element.data('url');
    $.spinner().start();
    $.ajax({
        url: showMoreUrl,
        method: 'GET',
        success: function (response) {
            $target.append(response);
            initPopOver();
            $.spinner().stop();
        },
        error: function () {
            $.spinner().stop();
        }
    });
}

/**
 * Update sort option URLs from Ajax response
 *
 * @param {string} response - Ajax response HTML code
 * @return {undefined}
 */
function updateSortOptions(response) {
    var $tempDom = $('<div>').append($(response));
    var sortOptions = $tempDom.find('.grid-footer').data('sort-options').options;
    sortOptions.forEach(function (option) {
        var optUrl = option.url ? option.url.toString() : '';
        var queryStartIndex = optUrl.indexOf('?');
        var urlParams = queryStartIndex >= 0 ? optUrl.substring(queryStartIndex + 1) : '';
        $('option.' + option.id).attr('data-ajax-params', urlParams);
    });
}

/**
 * Toggles the collapse state of the refinement category based on screen width and stored state.
 * Automatically collapses the category on mobile devices if no explicit state is stored.getSitePathFromUrl
 */
function toggleRefinementCollapse() {
    const categoryTabId = 'refinement-category';
    const categoryTab = document.getElementById(categoryTabId);
    const storedStates = JSON.parse(localStorage.getItem('refinementsState')) || {};

    if (breakpoints.down('lg') && storedStates[categoryTabId] === undefined) {
        $(categoryTab).collapse('hide');
    }
}

function initRangeSlider(rangeSlider, $inputFrom, $inputTo) {
    if (!rangeSlider
        || !$inputFrom.length
        || !$inputTo.length
        || !noUiSlider
        || !rangeSlider.hasAttribute('data-min')
        || !rangeSlider.hasAttribute('data-max')
    ) {
        return;
    }

    const currentMin = parseInt($inputFrom.val());
    const currentMax = parseInt($inputTo.val());
    const min = parseInt(rangeSlider.getAttribute('data-min'));
    const max = parseInt(rangeSlider.getAttribute('data-max'));
    const minLabel = rangeSlider.getAttribute('data-min-label');
    const maxLabel = rangeSlider.getAttribute('data-max-label');

    noUiSlider.create(rangeSlider, {
        start: [currentMin, currentMax],
        connect: true,
        range: {
            min,
            max
        }
    });

    if (minLabel) {
        rangeSlider.querySelector('.noUi-handle-lower')?.setAttribute('aria-label', minLabel);
    }

    if (maxLabel) {
        rangeSlider.querySelector('.noUi-handle-upper')?.setAttribute('aria-label', maxLabel);
    }

    rangeSlider.noUiSlider.on('update', (values) => {
        const newMin = parseInt(values[0]);
        const newMax = parseInt(values[1]);

        $inputFrom.val(newMin);
        $inputTo.val(newMax);
    });

    rangeSlider.noUiSlider.on('change', (() => {
        $inputTo.trigger('change');
    }));
}

function initPriceRangeSlider() {
    const priceSlider = document.getElementById('priceSlider');
    const $inputFrom = $('#price-refinement-pmin');
    const $inputTo = $('#price-refinement-pmax');

    initRangeSlider(priceSlider, $inputFrom, $inputTo);
}

function initAttributeRangeSliders() {
    $('.js-attribute-range-slider-container').each(function () {
        const $container = $(this);
        const $inputFrom = $container.find('.js-range-slider-input-from:first');
        const $inputTo = $container.find('.js-range-slider-input-to:first');
        const slider = $container[0].querySelector('.js-attribute-range-slider');

        initRangeSlider(slider, $inputFrom, $inputTo);
    });
}

function addSearchResultTextPlaceholder() {
    $('.js-results-count').addClass('placeholder-glow');
    $('.js-search-result-text').addClass('placeholder');
}

function removeSearchResultTextPlaceholder() {
    $('.js-results-count').removeClass('placeholder-glow');
    $('.js-search-result-text').removeClass('placeholder');
}

/**
 * Builds an AJAX URL by combining a base URL (stored once on .grid-footer)
 * with a query string. This avoids rendering full demandware URLs in HTML attributes.
 * @param {string} urlParams - query string without the leading '?'
 * @param {string} [baseDataAttr] - dataset key on .grid-footer for the base URL (default: 'searchAjaxUrl')
 * @returns {string} full AJAX URL
 */
function buildAjaxUrl(urlParams, baseDataAttr) {
    var attrName = baseDataAttr || 'searchAjaxUrl';
    var base = document.querySelector('.grid-footer')?.dataset?.[attrName] || '';
    return urlParams ? (base + '?' + urlParams) : base;
}

function applyfilterCall(e, url, resetLastAppliedRefinement = false) {
    const event = new CustomEvent('search:filtered');

    e.preventDefault();
    e.stopPropagation();

    const searchTriggerElement = resetLastAppliedRefinement ? null : e.target;
    updateLastAppliedRefinementId(searchTriggerElement);

    document.querySelectorAll('.js-refinement[data-refinement-attribute-id]').forEach(refinement => {
        if (lastAppliedRefinementId === refinement?.getAttribute('data-refinement-attribute-id')) {
            refinement.classList.add('pe-none');
        } else {
            refinementPlaceholder(refinement);
        }
    });

    const additionalDispatchDataOnUpdateUrl = e?.detail?.additionalDispatchDataOnUpdateUrl || {};

    const isFilterResetButton = e?.target?.classList?.contains('js-refinement-bar-reset-btn');

    const productGrid = document.querySelector('.js-product-grid');

    const scrollTargetElement = document.querySelector('.js-listing-content-start');
    const includeHeaderHeight = true;

    if (productGrid && scrollTargetElement) {
        productGrid.innerHTML = productTilePlaceholder(20);
        scrollToElement(scrollTargetElement, includeHeaderHeight);
    }
    addSearchResultTextPlaceholder();

    const $sortEle = $('.js-sort-order')?.find(':selected');
    const srule = $sortEle.data('ruleid');
    const updatedUrl = srule ? addAttributeToUrl(url, 'srule', srule) : url;

    $(this).trigger('search:filter', e);
    $.ajax({
        url: updatedUrl,
        data: {
            page: $('.grid-footer').data('page-number')
        },
        method: 'GET',
        success: function (response) {
            if (handleSearchRedirect(response)) {
                return;
            }
            parseResults(response, srule, additionalDispatchDataOnUpdateUrl);
            initPopOver();
            document.body.dispatchEvent(new CustomEvent('product:tileRendered', { bubbles: true }));
            initPriceRangeSlider();
            initAttributeRangeSliders();
            removeSearchResultTextPlaceholder();
            variantImgHover();
            if (isFilterResetButton && breakpoints.down('md')) {
                $('.refinement-bar, .modal-background').hide();
            }
            document.dispatchEvent(event);
        },
        error: function () {}
    });
}

function handleRangeChange($inputFrom, $inputTo, $slider) {
    let max = parseInt($slider.attr('data-max'));
    let newMin = parseInt($inputFrom.val());
    let newMax = parseInt($inputTo.val());

    if (newMin === newMax && newMin === max) {
        $inputFrom.val(newMin - 1);
        $inputTo.val(newMin);
    } else if (newMin === newMax) {
        $inputFrom.val(newMin);
        $inputTo.val(newMin + 1);
    }
}

function getCTSCategoryData(categoryLink, excludeID = null, isParent = false) {
    if (!categoryLink) {
        return null;
    }

    const categoryID = categoryLink.dataset.catId;
    if (excludeID === categoryID) {
        return null;
    }

    const text = categoryLink.querySelector('.js-refinement-text').cloneNode(true);
    text.querySelector('.js-refinement-count')?.remove();

    return {
        name: text.textContent.trim(),
        url: categoryLink.href,
        id: categoryID,
        imgUrl: categoryLink.dataset.heroCategoryImage,
        isParent: isParent
    };
}

function getCTSChildCategoriesData(categoryLink, excludeID) {
    if (!categoryLink) {
        return null;
    }

    const childWrapper = categoryLink.parentElement.querySelector(':scope > .js-refinement-child-wrapper');
    if (!childWrapper) {
        return null;
    }

    const childLinks = childWrapper.querySelectorAll(':scope > ul > li > .js-refinement-link');

    return [...childLinks].map(link => getCTSCategoryData(link, excludeID)).filter(Boolean);
}

function getCTSSlides(categoryListData) {
    if (!categoryListData) {
        return null;
    }

    const slides = categoryListData
        .map(category => `
            <div class="swiper-slide w-auto ${category.isParent ? 'pe-4 border-end' : ''}">
                <a href="${category.url}" class="text-decoration-none d-block h-100">
                    <div class="category-thumbnail-tile h-100">
                        <div class="mb-2 ratio ratio-1x1">
                            <img src="${imageUrl.generateImageUrl(category.imgUrl, { sw: 140 })}"
                                alt="${category.name}"
                                sizes="(min-width: 768px) 140px, 60px"
                                srcset="${imageUrl.generateImageSrcSet(category.imgUrl, [140, 280])}"
                                width="140" height="140"
                                class="img-fluid rounded-circle border"
                                loading="lazy"
                            />
                        </div>
                        <div class="category-thumbnail-text text-center text-body-emphasis">${category.name}</div>
                    </div>
                </a>
            </div>
        `).join('');

    return slides;
}

function getCTSCategoryListData(activeCategoryID) {
    if (!activeCategoryID) {
        return null;
    }

    const activeCategoryLink = document.querySelector(`.js-refinement-link.selected[data-cat-id="${activeCategoryID}"]`);
    const activeCategoryParentLink = activeCategoryLink
        ?.closest('.js-refinement-item')?.parentElement
        ?.closest('.js-refinement-item')?.querySelector('.js-refinement-link.selected');

    const activeCategoryParentData = getCTSCategoryData(activeCategoryParentLink, null, true);
    const activeCategoryChildrenData = getCTSChildCategoriesData(activeCategoryLink, activeCategoryID);
    let activeCategorySiblingsData = [];

    if (!activeCategoryChildrenData) {
        activeCategorySiblingsData = getCTSChildCategoriesData(activeCategoryParentLink, activeCategoryID);
    }

    // CategoryListData = Cildren || Siblings
    const categoryListData = [
        ...(activeCategoryParentData ? [activeCategoryParentData] : []),
        ...(activeCategoryChildrenData || activeCategorySiblingsData)
    ];

    return categoryListData;
}

/**
 * CTS - Category Thumbnail Slider,
 * Displays either Child || Sibling Categories
 */
function initCTS() {
    const categoryThumbnailsWrapper = document.querySelector('.js-category-thumbnails-wrapper');
    if (!categoryThumbnailsWrapper) {
        return;
    }

    // Slider Config
    const swiperElement = categoryThumbnailsWrapper.querySelector('.swiper');
    let sliderConfig;
    try {
        sliderConfig = JSON.parse(swiperElement.getAttribute('data-slider-config'));
    } catch {
        return;
    }

    // Collect Category Data
    const activeCategoryID = categoryThumbnailsWrapper.dataset.catId;
    const categoryListData = getCTSCategoryListData(activeCategoryID);

    // Build Slider
    const swiperSlides = getCTSSlides(categoryListData);
    categoryThumbnailsWrapper.querySelector('.js-category-thumbnails-placeholder')?.remove();
    const swiperWrapper = swiperElement.querySelector('.swiper-wrapper');
    swiperWrapper.classList.add('pt-2');
    swiperWrapper.innerHTML = swiperSlides;
    swiperElement.swiperInstance = new Swiper(swiperElement, sliderConfig);
}

module.exports = {
    init: function () {
        initPriceRangeSlider();
        initAttributeRangeSliders();
        variantImgHover();
        handleSeoTopTruncation();
        initCTS();
        syncMobileAppliedFilters();
        updateNoindexNofollow();
    },

    filter: function () {
        // Display refinements bar when Menu icon clicked
        $('.container').on('click', 'button.filter-results', function () {
            toggleRefinementCollapse();
            $('.refinement-bar, .modal-background').show();
            $('.refinement-bar').siblings().attr('aria-hidden', true);
            $('.refinement-bar').closest('.row').siblings().attr('aria-hidden', true);
            $('.refinement-bar').closest('.tab-pane.active').siblings().attr('aria-hidden', true);
            $('.refinement-bar').closest('.container.search-results').siblings().attr('aria-hidden', true);
            $('.refinement-bar .close').focus();
        });
    },

    closeRefinements: function () {
        // Refinements close button
        $('.container').on('click', '.refinement-bar button.close, .modal-background', function () {
            $('.refinement-bar, .modal-background').hide();
            $('.refinement-bar').siblings().attr('aria-hidden', false);
            $('.refinement-bar').closest('.row').siblings().attr('aria-hidden', false);
            $('.refinement-bar').closest('.tab-pane.active').siblings().attr('aria-hidden', false);
            $('.refinement-bar').closest('.container.search-results').siblings().attr('aria-hidden', false);
            $('.btn.filter-results').focus();
        });
    },

    resize: function () {
        // Close refinement bar and hide modal background if user resizes browser
        window.onresize = throttle(() => {
            $('.refinement-bar').hide();
            $('.modal-background').hide();

            if (breakpoints.up('lg')) {
                $('.refinement-bar').show();
                $('.modal-background').hide();
            }
        }, 100);
    },

    sort: function () {
        // Handle sort order menu selection
        $('.container').on('change', '[name=sort-order]', function (e) {
            e.preventDefault();
            var sortParams = $(this).find(':selected').data('ajax-params');
            if (!sortParams) {
                return;
            }
            var sortUrl = buildAjaxUrl(sortParams, 'gridAjaxUrl');
            document.getElementById('sort-order-option-label')?.remove();
            $('.product-grid').empty().html(productTilePlaceholder(20));
            $(this).trigger('search:sort', sortUrl);
            $.ajax({
                url: sortUrl,
                data: { selectedUrl: sortUrl },
                method: 'GET',
                success: function (response) {
                    if (handleSearchRedirect(response)) {
                        return;
                    }
                    // note: jquery html() method can execute <script> from response (e.g. tracking script)
                    // (js innerHTML would render it as plain text e.g <script rendered as &lt;script)
                    $('.product-grid').empty().html(response);

                    document.body.dispatchEvent(new CustomEvent('product:tileRendered', { bubbles: true }));
                    initPopOver();
                    document.dispatchEvent(new CustomEvent('search:filtered'));
                    $.spinner().stop();
                    updateAjaxURL($(response), null, null);
                },
                error: function () {}
            });
        });
    },

    pagination: function () {
        document.querySelectorAll('.container').forEach((container) => {
            container.addEventListener('click', function (e) {
                if (e.target.matches('.pagination .page-item a')) {
                    e.stopPropagation();
                    e.preventDefault();

                    var showMoreUrl = buildAjaxUrl(e.target.getAttribute('data-ajax-params'), 'gridAjaxUrl');

                    const productGridSidebar = document.querySelector('.js-product-grid');
                    if (productGridSidebar) {
                        productGridSidebar.innerHTML = productTilePlaceholder(20);
                    }

                    const listingPageStartElement = document.querySelector('.js-listing-content-start');
                    if (listingPageStartElement) {
                        scrollToElement(listingPageStartElement);
                    }

                    // Trigger a custom event 'search:showMore' on the clicked element
                    var showMoreEvent = new Event('search:showMore');
                    e.target.dispatchEvent(showMoreEvent);

                    // Use the Fetch API to send an AJAX request
                    fetch(showMoreUrl)
                        .then((response) => {
                            if (response.ok) {
                                return response.text();
                            }

                            return false;
                        })
                        .then((data) => {
                            if (handleSearchRedirect(data)) {
                                return;
                            }
                            if (productGridSidebar) {
                                // note: jquery html() method can execute <script> from response (e.g. tracking script)
                                // (js innerHTML would render it as plain text e.g <script rendered as &lt;script)
                                $(productGridSidebar).html(data);
                            }
                            updateSortOptions(data);
                            updateAjaxURL($(data), null, null);
                            initPopOver();
                            document.body.dispatchEvent(new CustomEvent('product:tileRendered', { bubbles: true }));
                            variantImgHover();
                            scrollToElement(listingPageStartElement);

                            const jsonLdScript = document.querySelector('head script[type="application/ld+json"]');
                            const schemaData = document.querySelector('.schemaData');
                            if (jsonLdScript && schemaData) {
                                jsonLdScript.innerHTML = schemaData.innerHTML;
                                schemaData.remove();
                            }
                            document.dispatchEvent(new Event('search:pagination-changed'));
                        })
                        .catch(() => {});
                }
            });
        });
    },

    applyPriceFilter: function () {
        $('.container').on(
            'change',
            '#price-refinement-pmin , #price-refinement-pmax',
            function (e) {
                const $inputFrom = $('#price-refinement-pmin');
                const $inputTo = $('#price-refinement-pmax');
                const $slider = $('#priceSlider');

                if (!$inputFrom || !$inputTo || !$slider) {
                    return;
                }

                handleRangeChange($inputFrom, $inputTo, $slider);

                let newMin = parseInt($inputFrom.val());
                let newMax = parseInt($inputTo.val());
                const url = new URL(buildAjaxUrl($('#price-refinement').data('ajax-params')), document.baseURI);

                url.searchParams.set('pmin', newMin.toString().replace('.', ','));
                url.searchParams.set('pmax', newMax.toString().replace('.', ','));

                applyfilterCall(e, url.toString());
            }
        );
    },

    applyFilter: function () {
        // Handle refinement value selection
        $('.container').on(
            'click',
            '.js-refinements li button, .swatch-filter button',
            function (e) {
                applyfilterCall(e, buildAjaxUrl($(this).data('ajax-params')));
            }
        );
    },

    initRangeFiltersFieldsChange: function () {
        $('.container').on(
            'change',
            '.js-range-slider-input',
            function (e) {
                const $filterRootElem = $(this).closest('[data-ajax-params]');
                const filterRootElemParams = $filterRootElem.data('ajax-params');
                const refinementAttributeKey = 'data-refinement-attribute-id';
                const refinementAttributeId = this.closest(`[${refinementAttributeKey}]`)?.getAttribute(refinementAttributeKey);
                if (!filterRootElemParams || !refinementAttributeId) {
                    return;
                }
                const $inputFrom = $filterRootElem.find('.js-range-slider-input-from:first');
                const $inputTo = $filterRootElem.find('.js-range-slider-input-to:first');
                const $slider = $filterRootElem.find('.js-attribute-range-slider:first');

                if (!$inputFrom || !$inputTo || !$slider) {
                    return;
                }

                handleRangeChange($inputFrom, $inputTo, $slider);

                let newMin = parseInt($inputFrom.val());
                let newMax = parseInt($inputTo.val());
                const url = new URL(buildAjaxUrl(filterRootElemParams), document.baseURI);

                let urlKey = '';
                let params = new URLSearchParams(filterRootElemParams);
                let preferenceCounter = 1;
                params.forEach((value, key)=> {
                    if (value === refinementAttributeId) {
                        urlKey = key;
                    } else if (key.indexOf('prefn') > -1) {
                        preferenceCounter += 1;
                    }
                });
                let urlKeyIndex;
                if (urlKey) {
                    urlKeyIndex = urlKey.toString().replace('prefn', '');
                } else {
                    urlKeyIndex = preferenceCounter;
                    url.searchParams.set(`prefn${preferenceCounter}`, refinementAttributeId);
                }

                url.searchParams.set(`prefmin${urlKeyIndex}`, newMin.toString().replace('.', ','));
                url.searchParams.set(`prefmax${urlKeyIndex}`, newMax.toString().replace('.', ','));

                applyfilterCall(e, url.toString());
            }
        );
    },

    initRunSearchCustomListener: function () {
        document.addEventListener('run-catalog-search', function (e) {
            const url = e?.detail?.url;
            const additionalDispatchDataOnUpdateUrl = e?.detail?.additionalDispatchDataOnUpdateUrl;
            if (url && additionalDispatchDataOnUpdateUrl) {
                applyfilterCall(e, url, true);
            }
        });
    },

    resetFilter: function () {
        // Handle refinement reset click
        $('.js-search-results').on(
            'click',
            '.js-search-reset-btn, .js-filter-value-btn, .js-refinement-bar-reset-btn',
            function (e) {
                let url = buildAjaxUrl($(this).data('ajax-params'));
                applyfilterCall(e, url, true);
            }
        );
    },

    showContentTab: function () {
        // Display content results from the search
        $('.container').on('click', '.content-search', function () {
            if ($('#content-search-results').html() === '') {
                getContent($(this), $('#content-search-results'));
            }
        });

        // Display the next page of content results from the search
        $('.container').on('click', '.show-more-content button', function () {
            getContent($(this), $('#content-search-results'));
            $('.show-more-content').remove();
        });
    },

    viewToggler: function () {
        const VIEW_TYPE_LIST = 'list';
        const VIEW_TYPE_GRID = 'grid';
        const ACTIVE_CLASS_NAME = 'active';

        const viewToggler = document.querySelector('.js-view-toggler');

        if (!viewToggler) {
            return;
        }

        const viewButtons = viewToggler?.querySelectorAll('.js-view-toggler-btn');
        const { classtotoggle, target, userpeference } = viewToggler.dataset;
        const targetElement = document.querySelector(target);

        if (!targetElement || !viewButtons || !targetElement || !target || !userpeference) {
            return;
        }

        if (localStorage.getItem(userpeference) === VIEW_TYPE_LIST || (localStorage.getItem(userpeference) === null && targetElement.dataset.viewType === VIEW_TYPE_LIST)) {
            targetElement.classList.add(classtotoggle);
            viewButtons.forEach(viewType => {
                if (viewType.dataset?.view === VIEW_TYPE_LIST) {
                    viewType.classList.add(ACTIVE_CLASS_NAME);
                } else {
                    viewType.classList.remove(ACTIVE_CLASS_NAME);
                }
            });
        }

        viewButtons.forEach((view) => {
            view.addEventListener('click', (event) => {
                event.preventDefault();
                const selectedViewButton = event.target.closest('.js-view-toggler-btn');

                if (!selectedViewButton) {
                    return;
                }

                viewButtons.forEach(viewType => viewType.classList.remove(ACTIVE_CLASS_NAME));
                selectedViewButton.classList.add(ACTIVE_CLASS_NAME);
                const selectedView = selectedViewButton.dataset.view;

                if (selectedView === VIEW_TYPE_LIST) {
                    targetElement.classList.add(classtotoggle);
                    localStorage.setItem(userpeference, VIEW_TYPE_LIST);
                } else {
                    targetElement.classList.remove(classtotoggle);
                    localStorage.setItem(userpeference, VIEW_TYPE_GRID);
                }
            });
        });
    },

    showMoreLink: function () {
        document.addEventListener('click', function (evt) {
            const target = evt.target.closest('.js-refinement-showmore-link');
            if (target) {
                evt.preventDefault();
                evt.stopPropagation();
                const refinementSection = target?.closest('.js-refinement');
                if (target?.dataset?.showmoreStatus === 'true') {
                    refinementHandleShowMore(refinementSection, target);
                } else {
                    refinementSection.querySelectorAll("[data-showmore='true']").forEach((item) => {
                        item.classList.add('d-none');
                    });
                    target.dataset.showmoreStatus = 'true';
                    target.innerText = `${evt.target?.dataset?.showmoreText}`;
                    scrollRefinementIntoViewport(refinementSection);
                }
            }
        });
    },

    filterModalClose: function () {
        document.querySelector('.js-filter-apply')?.addEventListener('click', ()=>{
            $('.refinement-bar, .modal-background').hide();
        });
    }
};
